// RITA-1498 (Nutzungserfassung Phase 1b, Epic RITA-1496) — der eine Einhängepunkt (Konzept 4.1).
//
// Sitzt in App.tsx innerhalb von BrowserRouter, SettingsProvider und UserProvider. Angemeldet
// wird GET /usage/config gelesen; nur bei "an" läuft die Erfassung (services/runtime). Der
// Zustand steht als data-usage-tracking="on|off" am <html> (nicht sichtbar; für Browser-Tests).
// Der Apollo-Link hängt getrennt in components/ErrorMessage/ErrorMessage.tsx, weil der
// Apollo-Client oberhalb des Routers entsteht; beide melden an denselben Tracker.
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import UserContext from '../../context/UserProvider/UserContext';
import { getToken } from '../../context/CommunicationProvider/authUser';
import { useSettingsContext } from '../../components/settings/SettingsContext';
import { fetchAppVersion, fetchUsageConfig } from '../../features/usage-tracking/services/configClient';
import { resolveEnv } from '../../features/usage-tracking/services/deviceInfo';
import { createInteractionHints } from '../../features/usage-tracking/services/navigation';
import { startUsageRuntime } from '../../features/usage-tracking/services/runtime';
import { usageTracker, UsageTracker } from '../../features/usage-tracking/services/tracker';
import { usePageViewTracking } from '../../features/usage-tracking/hooks/usePageViewTracking';
import { useSettingsUsage } from '../../features/usage-tracking/hooks/useSettingsUsage';
import { TrackAction, UsageTrackingContext } from '../../features/usage-tracking/hooks/useTrack';

export const USAGE_STATE_ATTRIBUTE = 'data-usage-tracking';
const MAX_ACTION_NAME_LENGTH = 128;

type FetchImpl = (input: string, init?: RequestInit) => Promise<Response>;

interface Props {
  children: React.ReactNode;
  tracker?: UsageTracker;
  fetchImpl?: FetchImpl;
}

interface Setup {
  enabled: boolean;
  appVersion: string;
}

const defaultFetch: FetchImpl = (input, init) => window.fetch(input, init);

export function UsageTrackingProvider({ children, tracker = usageTracker, fetchImpl = defaultFetch }: Props) {
  const user = useContext(UserContext);
  const settings = useSettingsContext();
  const loggedIn = Number(user.id) > 0 && !!getToken();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [active, setActive] = useState(false);
  const hints = useRef(createInteractionHints()).current;
  const mountedRef = useRef(true);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!loggedIn) {
      setSetup(null);
      return undefined;
    }
    let cancelled = false;
    Promise.all([fetchUsageConfig({ fetchImpl, getToken }), fetchAppVersion({ fetchImpl })])
      .then(([config, appVersion]) => {
        if (!cancelled) setSetup({ enabled: config.enabled, appVersion });
      })
      .catch(() => {
        if (!cancelled) setSetup({ enabled: false, appVersion: '' });
      });
    return () => {
      cancelled = true;
    };
  }, [loggedIn, fetchImpl]);

  useEffect(() => {
    const root = document.documentElement;
    if (!setup) {
      root.removeAttribute(USAGE_STATE_ATTRIBUTE);
      setActive(false);
      return undefined;
    }
    if (!setup.enabled) {
      root.setAttribute(USAGE_STATE_ATTRIBUTE, 'off');
      setActive(false);
      return undefined;
    }
    const runtime = startUsageRuntime({
      tracker,
      hints,
      fetchImpl,
      getToken,
      getSettings: () => settingsRef.current,
      appVersion: setup.appVersion,
      env: resolveEnv({ hostname: window.location.hostname, nodeEnv: process.env.NODE_ENV }),
      win: window,
      doc: document,
      onStop: () => {
        root.setAttribute(USAGE_STATE_ATTRIBUTE, 'off');
        if (mountedRef.current) setActive(false);
      },
    });
    root.setAttribute(USAGE_STATE_ATTRIBUTE, 'on');
    setActive(true);
    return () => {
      runtime.stop();
      root.removeAttribute(USAGE_STATE_ATTRIBUTE);
      if (mountedRef.current) setActive(false);
    };
  }, [setup, tracker, fetchImpl, hints]);

  usePageViewTracking({ tracker, hints, active });
  // RITA-1585: Als Einstellungsänderung zählt nur die eigene Menü-Wahl, nicht die vorübergehende Darstellung bei
  // offenem Handbuch (`themeLayout` ist die wirksame Darstellung, die Heatmap-Filter weiter brauchen).
  useSettingsUsage({ tracker, hints, active, settings: { ...settings, themeLayout: settings.gespeichertesLayout } });

  // `enabled` gibt den GELESENEN Zustand weiter: null, solange keine Antwort vorliegt (nicht
  // angemeldet oder Konfiguration noch unterwegs). Verbraucher wie die Nutzungsseite dürfen
  // „Erfassung: ausgeschaltet“ nur bei einem bestätigten false behaupten — der Konfigurations-
  // Client ist fail-closed, jede Störung sähe sonst wie „abgeschaltet“ aus (RITA-1505).
  const value = useMemo(() => {
    const track: TrackAction = (name, props) => {
      tracker.record({ type: 'action', name: String(name).slice(0, MAX_ACTION_NAME_LENGTH), props });
    };
    return { track, enabled: setup === null ? null : setup.enabled };
  }, [tracker, setup]);

  return <UsageTrackingContext.Provider value={value}>{children}</UsageTrackingContext.Provider>;
}

export default UsageTrackingProvider;
